import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './Presidium.css'

const presidiumMembers = [
  {
    id: 'glushchenko',
    name: 'Глущенко Владимир Леонидович',
    nameEn: 'Vladimir Leonidovich Glushchenko',
    nameJa: 'グルシチェンコ・ウラジミール・レオニドヴィチ',
    position: 'Председатель',
    positionEn: 'Chairman',
    positionJa: '議長',
    country: 'Россия',
    countryEn: 'Russia',
    countryJa: 'ロシア',
    photo: '/images/persons/Glushchenko_Vladimir_Leonidovich.jpg'
  },
  {
    id: 'goleshev',
    name: 'Голешев Виталий Юрьевич',
    nameEn: 'Vitaly Yuryevich Goleshev',
    nameJa: 'ゴレシェフ・ヴィタリー・ユーリエヴィチ',
    position: 'Член президиума',
    positionEn: 'Presidium Member',
    positionJa: '幹部会員',
    country: 'Беларусь',
    countryEn: 'Belarus',
    countryJa: 'ベラルーシ',
    photo: '/images/persons/Goleshev_Vitaliy_Yurievich.jpg'
  },
  {
    id: 'demitrov',
    name: 'Демитров Красимир Михайлов',
    nameEn: 'Krasimir Mikhailov Demitrov',
    nameJa: 'デミトロフ・クラシミル・ミハイロフ',
    position: 'Член президиума',
    positionEn: 'Presidium Member',
    positionJa: '幹部会員',
    country: 'Болгария',
    countryEn: 'Bulgaria',
    countryJa: 'ブルガリア',
    photo: '/images/persons/Dimitrov_Krasimir_Mihailov.jpg'
  },
  {
    id: 'allahverdiev',
    name: 'Аллахвердиев Тарверди',
    nameEn: 'Tarvardi Allahverdiev',
    nameJa: 'アッラフヴェルディエフ・タルヴェルディ',
    position: 'Член президиума',
    positionEn: 'Presidium Member',
    positionJa: '幹部会員',
    country: 'Азербайджан',
    countryEn: 'Azerbaijan',
    countryJa: 'アゼルバイジャン',
    photo: '/images/persons/Allakhverdiev_Tarverdi.jpg'
  },
  {
    id: 'aliev',
    name: 'Алиев Вахид',
    nameEn: 'Vahid Aliev',
    nameJa: 'アリエフ・ヴァヒド',
    position: 'Член президиума',
    positionEn: 'Presidium Member',
    positionJa: '幹部会員',
    country: 'Азербайджан',
    countryEn: 'Azerbaijan',
    countryJa: 'アゼルバイジャン',
    photo: '/images/persons/Aliev_Vakhid.jpg'
  },
  {
    id: 'sofronov',
    name: 'Софронов Николай Викторович',
    nameEn: 'Nikolai Viktorovich Sofronov',
    nameJa: 'ソフロノフ・ニコライ・ヴィクトロヴィチ',
    position: 'Член президиума',
    positionEn: 'Presidium Member',
    positionJa: '幹部会員',
    country: 'Россия',
    countryEn: 'Russia',
    countryJa: 'ロシア',
    photo: '/images/persons/Sofronov_Nikolay_Viktorovich.jpg'
  },
  {
    id: 'makhnev',
    name: 'Махнев Михаил Сергеевич',
    nameEn: 'Mikhail Sergeevich Makhnev',
    nameJa: 'マフネフ・ミハイル・セルゲーエヴィチ',
    position: 'Член президиума',
    positionEn: 'Presidium Member',
    positionJa: '幹部会員',
    country: 'Россия',
    countryEn: 'Russia',
    countryJa: 'ロシア',
    photo: '/images/persons/Makhnev_Mikhail_Sergeevich.jpg'
  }
]

function Presidium() {
  const { t, i18n } = useTranslation()
  const isRussian = i18n.language === 'ru'
  const isJapanese = i18n.language === 'ja'

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.presidium'), 'presidium'),
    [t]
  )

  return (
    <div className="presidium-page">
      <SEO 
        title={t('common.presidium')}
        description={t('presidium.description', 'Presidium of the Supreme Council of SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="presidium-hero">
          <h1>{t('common.presidium')}</h1>
          <p className="presidium-intro">
            {t('presidium.intro', 'The Presidium of the Supreme Council is the governing body of SUMA International, responsible for strategic decisions and organizational leadership.')}
          </p>
        </div>

        <section className="presidium-section">
          <h2>{t('presidium.composition', 'Composition')}</h2>
          <p>
            {t('presidium.compositionText', 'The Presidium consists of experienced masters and leaders in martial arts, representing various disciplines and regions.')}
          </p>
        </section>

        <section className="presidium-section">
          <h2>{t('presidium.members', 'Members')}</h2>
          <div className="members-grid">
            {presidiumMembers.map(member => {
              const memberName = isJapanese ? (member.nameJa || member.nameEn) : (isRussian ? member.name : member.nameEn)
              return (
                <div key={member.id} className={`member-card ${member.id === 'glushchenko' ? 'chairman' : ''}`}>
                  {member.photo ? (
                    <div className="member-photo">
                      <img src={member.photo} alt={memberName} />
                    </div>
                  ) : (
                    <div className="member-placeholder">
                      <span>{t('presidium.memberPhoto', 'Photo')}</span>
                    </div>
                  )}
                  <h3>{memberName}</h3>
                  <p className="member-position">{isJapanese ? (member.positionJa || member.positionEn) : (isRussian ? member.position : member.positionEn)}</p>
                  <p className="member-country">{isJapanese ? (member.countryJa || member.countryEn) : (isRussian ? member.country : member.countryEn)}</p>
                </div>
              )
            })}
          </div>
        </section>

        <section className="presidium-section">
          <h2>{t('presidium.responsibilities', 'Responsibilities')}</h2>
          <ul className="responsibilities-list">
            <li>{t('presidium.responsibility1', 'Strategic planning and development of SUMA International')}</li>
            <li>{t('presidium.responsibility2', 'Oversight of federations and member schools')}</li>
            <li>{t('presidium.responsibility3', 'Certification and standards development')}</li>
            <li>{t('presidium.responsibility4', 'International relations and partnerships')}</li>
          </ul>
        </section>
      </div>
    </div>
  )
}

export default Presidium
